import { useEffect } from 'react';

/**
 * Manages focus-triggered scrolling for older mobile devices with known keyboard handling issues.
 *
 * ### Why This Hook Exists
 *
 * Older mobile browsers (pre-iOS 15 and pre-Chrome 94) don't automatically scroll input fields
 * into view when the virtual keyboard opens. This causes input fields to be hidden behind
 * the keyboard, making them unusable.
 *
 * Modern browsers (iOS 15+, Chrome 94+) handle this automatically, so this hook:
 * - Only activates on problematic mobile devices
 * - Only scrolls INPUT and TEXTAREA elements
 * - Prevents UI jump bugs by NOT scrolling buttons, accordions, tabs, or other clickable elements
 *
 * ### When You Need This
 *
 * You likely DON'T need this if:
 * - Your forms are small and centered in dialogs (like SharePopover)
 * - Your inputs are positioned near the top of the viewport
 * - You only support modern devices (most users in 2024/2025)
 *
 * You DO need this if:
 * - You have long scrollable forms with inputs at the bottom
 * - You need to support older Android devices (pre-2021)
 * - You have explicit user reports of keyboard covering inputs
 *
 * ### What It Prevents
 *
 * By only scrolling text input elements, this hook prevents:
 * - Accordions scrolling when clicked (causing UI jump)
 * - Filter tabs scrolling when clicked (causing missed clicks)
 * - List items scrolling when clicked (requiring double-click)
 * - Any non-input element causing unwanted scroll behavior
 *
 * ### Browser Support
 *
 * - iOS 15+ (Sept 2021): Handles this natively ✅
 * - Chrome 94+ (Sept 2021): Handles this natively ✅
 * - Older devices: This hook provides the fix ✅
 *
 * @example
 * ```typescript
 * // In App.tsx or your root component
 * const App: React.FC = () => {
 *   useMobileKeyboardFocusScroll();
 *
 *   return <RouterProvider router={router} />;
 * };
 * ```
 */
export const useMobileKeyboardFocusScroll = () => {
	useEffect(() => {
		// Only for old mobile devices with known keyboard handling issues
		const ua = navigator.userAgent;
		const isProblematicMobile =
			/iPhone|iPad|iPod|Android/i.test(ua) &&
			!/OS 1[5-9]_/.test(ua) && // iOS 15+ handles this natively
			!/Chrome\/(9[4-9]|[1-9][0-9]{2,})/.test(ua); // Chrome 94+ handles this natively

		if (!isProblematicMobile) return;

		console.warn('This browser is likely not supported...');

		let lastScrollY = 0;

		const handleFocusIn = (event: FocusEvent) => {
			const target = event.target as HTMLElement;

			// CRITICAL: Only scroll INPUT and TEXTAREA elements
			// This prevents UI jump bugs with accordions, tabs, buttons, and list items
			const isTextInput =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.getAttribute('contenteditable') === 'true';

			if (!isTextInput) return;

			lastScrollY = window.scrollY;
			target.scrollIntoView({ behavior: 'smooth', block: 'center' });
		};

		const handleFocusOut = () => {
			// Restore scroll position after keyboard closes
			setTimeout(() => window.scrollTo(0, lastScrollY), 50);
		};

		document.addEventListener('focusin', handleFocusIn);
		document.addEventListener('focusout', handleFocusOut);

		return () => {
			document.removeEventListener('focusin', handleFocusIn);
			document.removeEventListener('focusout', handleFocusOut);
		};
	}, []);
};
